import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * Modal to freeze/unfreeze tickets by serial number range (inclusive).
 * @param {'freeze'|'unfreeze'} mode
 */
function TicketRangeModal({ show, mode, onConfirm, onCancel, loading = false }) {
  const { t } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (show) {
      setFrom('');
      setTo('');
      setError('');
    }
  }, [show, mode]);

  const handleConfirm = () => {
    const fromNum = parseInt(from, 10);
    const toNum = parseInt(to, 10);
    if (!Number.isFinite(fromNum) || !Number.isFinite(toNum) || fromNum < 1 || toNum < fromNum) {
      setError(t('ticket.rangeInvalid'));
      return;
    }
    setError('');
    onConfirm(fromNum, toNum);
  };

  const title =
    mode === 'freeze' ? t('ticket.rangeModalTitleFreeze') : t('ticket.rangeModalTitleUnfreeze');
  const confirmLabel = mode === 'freeze' ? t('ticket.freezeRange') : t('ticket.unfreezeRange');

  return (
    <Dialog open={show} onOpenChange={open => !open && !loading && onCancel()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t('ticket.rangeHint')}</DialogDescription>
        </DialogHeader>

        <div className='grid grid-cols-2 gap-4 py-2'>
          <div className='space-y-2'>
            <label className='text-sm font-medium text-foreground' htmlFor='range-from'>
              {t('ticket.rangeFrom')}
            </label>
            <Input
              id='range-from'
              type='number'
              min={1}
              value={from}
              onChange={e => setFrom(e.target.value)}
              disabled={loading}
              placeholder='1'
            />
          </div>
          <div className='space-y-2'>
            <label className='text-sm font-medium text-foreground' htmlFor='range-to'>
              {t('ticket.rangeTo')}
            </label>
            <Input
              id='range-to'
              type='number'
              min={1}
              value={to}
              onChange={e => setTo(e.target.value)}
              disabled={loading}
              placeholder='10000'
            />
          </div>
        </div>

        {error && <p className='text-sm text-destructive'>{error}</p>}

        <DialogFooter>
          <Button variant='outline' onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={loading}>
            {loading ? t('common.loading') : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TicketRangeModal;
